import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { jcqAPI } from '../services/api';
import { Form8Modal } from './JCQNormalWayOfWorking';
import { BlueZoneBar, JCQPerformanceTrackerChart } from './JCQExtraTimeTest';

const BRAND = {
  teal: '#75B9BE',
  cream: '#FDF6EC',
  charcoal: '#2D4042',
  coral: '#FF7F50',
  white: '#FFFFFF',
  muted: '#5D7375'
};

const getChecklistStorageKey = () => `jcqStudentChecklist_${auth.currentUser?.uid || 'anon'}`;

const FORM8_ITEMS = [
  { key: 'form8Part1', label: 'Form 8 Part 1 completed — tick when in file' },
  { key: 'form8Part2', label: 'Form 8 Part 2 completed, signed & dated by the assessor' },
  { key: 'form8Part3', label: 'Form 8 Part 3 completed and the Declaration signed and dated' },
  { key: 'form8Evidence25', label: 'Accompanying samples of evidence for 25% extra time' },
  { key: 'form8AssessorQual', label: 'Assessor qualification evidence' }
];

const FORM8RF_ITEMS = [
  { key: 'form8rfCompleted', label: 'Form 8RF completed, signed and dated' }
];

const FORM9_ITEMS = [
  { key: 'form9Completed', label: 'Form 9 completed, signed & dated' },
  { key: 'form9SpecialistEvidence', label: 'Specialist evidence (not required for computer reader/reader)' },
  { key: 'form9Evidence25', label: 'Accompanying samples of evidence for 25% extra time' }
];

const AAO_ITEMS = [
  { key: 'aaoApproval', label: 'AAO Approval received and in file' }
];

const ROUTE_OPTIONS = [
  { value: 'form8', label: 'Form 8 route' },
  { value: 'form8rf', label: 'Form 8RF route' },
  { value: 'form9', label: 'Form 9 route' }
];

const getSectionsForRoute = (route) => {
  if (route === 'form8') {
    return [
      { title: 'Form 8 route', items: FORM8_ITEMS },
      { title: 'AAO Approval', items: AAO_ITEMS }
    ];
  }
  if (route === 'form8rf') {
    return [
      { title: 'Form 8RF route', items: [...FORM8_ITEMS, ...FORM8RF_ITEMS] },
      { title: 'AAO Approval', items: AAO_ITEMS }
    ];
  }
  if (route === 'form9') {
    return [
      { title: 'Form 9 route', items: FORM9_ITEMS },
      { title: 'AAO Approval', items: AAO_ITEMS }
    ];
  }
  return [];
};

const getCheckKeysForRoute = (route) => getSectionsForRoute(route).flatMap((section) => section.items.map((item) => item.key));

// Demo mode: these items are always ticked and open a popup of the corresponding document.
const DEMO_ITEMS = {
  form8Part1: { popupType: 'form8Part1', buttonLabel: 'View Form 8 Part 1' },
  form8Evidence25: { popupType: 'evidence25', buttonLabel: 'View evidence samples' },
  form9Evidence25: { popupType: 'evidence25', buttonLabel: 'View evidence samples' }
};

const P = {
  overlay: { alignItems: 'flex-start', background: 'rgba(45, 64, 66, 0.55)', display: 'flex', inset: 0, justifyContent: 'center', overflowY: 'auto', padding: '40px 16px', position: 'fixed', zIndex: 1000 },
  paper: { background: '#fff', borderRadius: '6px', boxShadow: '0 18px 50px rgba(0, 0, 0, 0.35)', boxSizing: 'border-box', color: '#222', fontFamily: "'Inter', sans-serif", maxWidth: '840px', padding: '34px 38px', width: '100%' },
  header: { borderBottom: '2px solid #333', marginBottom: 16, paddingBottom: 10 },
  sectionBar: { background: '#333', color: '#fff', fontSize: '0.8rem', fontWeight: 700, margin: '18px 0 10px', padding: '6px 10px', textTransform: 'uppercase' },
  fieldLabel: { color: '#444', fontSize: '0.72rem', fontWeight: 700, marginBottom: 3 },
  fieldBox: { border: '1px solid #555', fontSize: '0.85rem', lineHeight: 1.5, minHeight: 52, padding: '8px 10px', whiteSpace: 'pre-wrap' }
};

const PInfoRow = ({ cells }) => (
  <div style={{ border: '1px solid #555', display: 'grid', gridTemplateColumns: cells.map(() => '1fr').join(' '), marginBottom: 8 }}>
    {cells.map((cell, i) => (
      <div key={cell.label} style={{ borderRight: i < cells.length - 1 ? '1px solid #555' : 'none', padding: '5px 8px' }}>
        <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>{cell.label}</div>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: 1 }}>{cell.value || ''}</div>
      </div>
    ))}
  </div>
);

// One demo template shared by every student — only the candidate name changes.
const DEMO_BASELINE = { senConditions: ['Dyslexia'], eal: 'No', audioNarrationEnabled: false };

const buildDemoDifficultiesText = (name = 'The candidate') =>
  `${name} works at a significantly slower rate than their peers, associated with their Dyslexia. Platform tracking across 42 internal assessments shows the candidate taking an average of 31% longer than the peer cohort to process and complete timed tasks, with the size of the deficit varying by subject: 38% longer in Extended Writing, 29% longer in STEM and 26% longer in Applied subjects. In internal tests and mock examinations sat under standard timing, the candidate runs out of time, leaving later questions rushed or unattempted, so marks under-represent the understanding demonstrated in untimed classwork. Subject teachers and support staff report the same pattern across subjects: accurate work produced at a markedly slower pace than the rest of the class.`;

const buildDemoNWoWText = (name = 'the candidate') =>
  `25% extra time has been ${name}'s established normal way of working in the centre for the past two academic years. It is applied as standard across regular homework tasks, in-class tests and pre-teaching activities, as well as in internal tests and mock examinations. The attached audit trail records this history of need and provision across subjects. With the additional time in place the candidate completes tasks in full and achieves results in line with their classwork; without it, performance deteriorates in line with the difficulties described above.`;

const mkDemoAnswers = (standard, extraTime) => [
  ...standard.map(([timeTaken, isCorrect]) => ({ timeTaken, isCorrect, isExtraTimePhase: false })),
  ...extraTime.map(([timeTaken, isCorrect]) => ({ timeTaken, isCorrect, isExtraTimePhase: true }))
];

const formatDemoSubmissionDate = (daysAgo) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const DEMO_EVIDENCE_SUBJECTS = [
  {
    subject: 'Extended Writing — English Comprehension',
    submittedDaysAgo: 20,
    standardMaxSecs: 900,
    blueZone: { standardTimeTotal: 10, standardTimeCorrect: 7, extraTimeTotal: 2, extraTimeCorrect: 2, totalQuestions: 12, averageTimeSecs: 81 },
    answers: mkDemoAnswers(
      [[82, true], [88, true], [79, true], [91, true], [86, true], [98, false], [84, true], [92, true], [95, false], [90, false]],
      [[68, true], [64, true]]
    ),
    note: 'Questions 1–8 answered accurately within standard time; accuracy drops sharply as the standard-time limit approaches. Both remaining questions were completed correctly within the 25% extra time window, with response quality matching the opening answers.'
  },
  {
    subject: 'STEM — Science Problem Solving',
    submittedDaysAgo: 13,
    standardMaxSecs: 900,
    blueZone: { standardTimeTotal: 11, standardTimeCorrect: 8, extraTimeTotal: 1, extraTimeCorrect: 1, totalQuestions: 12, averageTimeSecs: 76 },
    answers: mkDemoAnswers(
      [[74, true], [81, true], [77, true], [85, true], [72, true], [88, false], [79, true], [83, true], [90, false], [86, true], [78, false]],
      [[71, true]]
    ),
    note: 'Recall and application questions are secure throughout, but multi-step calculation questions late in the paper were rushed under standard timing. The final question was completed correctly in extra time with full working shown.'
  },
  {
    subject: 'Applied — Geography Case Study',
    submittedDaysAgo: 6,
    standardMaxSecs: 900,
    blueZone: { standardTimeTotal: 10, standardTimeCorrect: 8, extraTimeTotal: 2, extraTimeCorrect: 2, totalQuestions: 12, averageTimeSecs: 74 },
    answers: mkDemoAnswers(
      [[70, true], [76, true], [82, true], [73, true], [79, true], [85, true], [77, false], [88, true], [92, false], [81, true]],
      [[66, true], [72, true]]
    ),
    note: 'Extended response questions show the same processing-speed pattern in a third subject area, confirming the difficulty is generalised rather than subject-specific, as JCQ requires.'
  }
];

const DEMO_EXTENDED_WRITING = {
  question: '"Our sense of place is shaped more by the people around us than by the landscape itself." To what extent do you agree with this view? Support your answer with detailed reference to the texts you have studied.',
  marks: 15,
  durationMinutes: 15,
  sourceSubject: 'English',
  response: 'I largely agree with this statement. In both texts the writers present community as the strongest influence on how characters understand where they belong. The landscape is described vividly, but it only gains meaning through the relationships attached to it.\n\nIn the opening chapters, the narrator’s attachment to the valley is framed entirely through her grandmother’s stories, suggesting that place is inherited socially rather than experienced directly. This foreshadows the narrator’s sense of dislocation later in the text, when the community disperses but the landscape itself is unchanged.',
  extraTimeResponse: 'In conclusion, both writers show that it is people who turn space into place. The final chapters confirm this: the valley remains physically untouched, yet the narrator describes herself as "nowhere" once the community has gone. The landscape endures; the sense of place does not — which is precisely the distinction the question asks us to weigh.',
  amendments: [
    {
      original: 'The writer uses lots of describing words to make the valley seem important.',
      amended: 'The writer’s cumulative natural imagery elevates the valley to a symbol of continuity, which makes the community’s departure in the final chapters feel like a rupture.',
      madeInExtraTime: true
    },
    {
      original: 'this shows she misses home',
      amended: 'This reveals that what the narrator misses is not the physical place but the web of relationships that gave it meaning — directly supporting the statement in the question.',
      madeInExtraTime: true
    }
  ],
  mark: 12,
  buildComment: (name = 'The candidate') =>
    `Planning and paragraph structure were completed within standard time. ${name} used the extra time window to finish the final paragraph and strengthen two analytical points rather than add new material — consistent with a processing-speed need rather than a gap in understanding.`
};

const Evidence25Popup = ({ student, onClose }) => {
  const currentYear = new Date().getFullYear();

  return (
    <div style={P.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={P.paper}>
        <div className="jcq-no-print" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 20 }}>
          <button className="btn-base btn-charcoal" onClick={() => window.print()}>🖨 Print / Save as PDF</button>
          <button className="btn-base btn-outline" onClick={onClose}>✕ Close</button>
        </div>

        <div style={P.header}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: '#555', fontSize: '0.68rem', fontWeight: 700 }}>JCQ Access Arrangements — Supporting evidence for Form 8 / Form 9</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 900, marginTop: 2 }}>Accompanying samples of evidence for 25% extra time</div>
            </div>
            <div style={{ color: '#555', fontSize: '0.68rem', textAlign: 'right' }}>Keep on file for<br />inspection purposes</div>
          </div>
        </div>

        <PInfoRow cells={[{ label: 'Candidate Name', value: student?.name }, { label: 'Evidence Basis', value: 'Timed tutorial assessments' }, { label: 'Academic Year', value: `${currentYear}–${currentYear + 1}` }]} />

        <div style={{ color: '#444', fontSize: '0.78rem', margin: '10px 0 4px' }}>
          JCQ requires evidence across Extended Writing, STEM, and Applied — confirming a generalised processing speed difficulty. Each chart plots cumulative accuracy against real elapsed time; the shaded boundary marks the end of standard time, with answers beyond it completed in the 25% extra time window.
        </div>

        {DEMO_EVIDENCE_SUBJECTS.map((sample) => (
          <div key={sample.subject}>
            <div style={P.sectionBar}>{sample.subject}</div>
            <div style={{ marginBottom: 10 }}>
              <BlueZoneBar stats={sample.blueZone} />
            </div>
            <JCQPerformanceTrackerChart stats={{ answers: sample.answers }} isExtraTimeStudent standardMaxSecs={sample.standardMaxSecs} />
            <div style={{ ...P.fieldBox, marginTop: 8, marginBottom: 4 }}>{sample.note}</div>
          </div>
        ))}

        <div style={P.sectionBar}>Extended Writing Test — Marked sample</div>
        <div style={{ background: '#F8FAFC', border: '2px solid #D8DEE2', borderRadius: 14, padding: 16, marginBottom: 12 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', marginBottom: 8 }}>
            <strong style={{ color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontSize: '1rem' }}>Writing Test Question</strong>
            <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
              <span style={{ background: BRAND.coral, borderRadius: 999, color: BRAND.white, fontSize: '0.75rem', fontWeight: 900, padding: '4px 10px' }}>{DEMO_EXTENDED_WRITING.marks} marks</span>
              <span style={{ background: BRAND.teal, borderRadius: 999, color: BRAND.white, fontSize: '0.75rem', fontWeight: 900, padding: '4px 10px' }}>
                {DEMO_EXTENDED_WRITING.durationMinutes} min · {Math.round(DEMO_EXTENDED_WRITING.durationMinutes * 1.25 * 100) / 100} min extra time
              </span>
            </div>
          </div>
          <div style={{ color: BRAND.charcoal, fontWeight: 800, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {DEMO_EXTENDED_WRITING.question}
          </div>
          <div style={{ color: '#5D7375', fontSize: '0.78rem', fontWeight: 800, marginTop: 8 }}>Source subject: {DEMO_EXTENDED_WRITING.sourceSubject}</div>
        </div>

        <div style={{ background: '#F9FAFB', border: '1.5px solid #D8DEE2', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ alignItems: 'center', background: BRAND.white, borderBottom: '1.5px solid #D8DEE2', display: 'flex', flexWrap: 'wrap', gap: 12, padding: '8px 12px' }}>
            <span style={{ color: '#5D7375', fontSize: '0.72rem', fontWeight: 900, letterSpacing: 0.5, textTransform: 'uppercase' }}>Typed response</span>
            <span style={{ alignItems: 'center', display: 'inline-flex', fontSize: '0.72rem', fontWeight: 800, gap: 6 }}>
              <span style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: 4, display: 'inline-block', height: 12, width: 12 }} />
              <span style={{ color: '#1D4ED8' }}>Written in extra time</span>
            </span>
            <span style={{ alignItems: 'center', display: 'inline-flex', fontSize: '0.72rem', fontWeight: 800, gap: 6 }}>
              <span style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 4, display: 'inline-block', height: 12, width: 12 }} />
              <span style={{ color: '#92400E' }}>{DEMO_EXTENDED_WRITING.amendments.length} amendments</span>
            </span>
            <span style={{ color: '#5D7375', fontSize: '0.75rem', fontWeight: 800, marginLeft: 'auto' }}>
              {`${DEMO_EXTENDED_WRITING.response} ${DEMO_EXTENDED_WRITING.extraTimeResponse}`.trim().split(/\s+/).length} words
            </span>
          </div>
          <div style={{ color: BRAND.charcoal, lineHeight: 1.6, padding: 12, whiteSpace: 'pre-wrap' }}>
            {DEMO_EXTENDED_WRITING.response}
            <mark style={{ background: '#DBEAFE', borderRadius: 4, boxShadow: '0 0 0 2px #DBEAFE', color: '#1E3A8A', fontWeight: 700 }}>
              {`\n\n${DEMO_EXTENDED_WRITING.extraTimeResponse}`}
            </mark>
          </div>
          <div style={{ background: '#FFFBEB', borderTop: '1.5px solid #FCD34D', padding: 12 }}>
            <div style={{ color: '#92400E', fontSize: '0.72rem', fontWeight: 900, letterSpacing: 0.5, marginBottom: 8, textTransform: 'uppercase' }}>Amendments made by student</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {DEMO_EXTENDED_WRITING.amendments.map((a, idx) => (
                <div key={idx} style={{ background: BRAND.white, border: '1px solid #FCD34D', borderRadius: 8, fontSize: '0.85rem', lineHeight: 1.5, padding: '8px 10px' }}>
                  <span style={{ color: '#9CA3AF', textDecoration: 'line-through' }}>{a.original}</span>
                  <span style={{ color: '#5D7375', fontWeight: 900, margin: '0 8px' }}>→</span>
                  <span style={{ color: BRAND.charcoal, fontWeight: 800 }}>{a.amended}</span>
                  {a.madeInExtraTime && (
                    <span style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: 999, color: '#1D4ED8', fontSize: '0.65rem', fontWeight: 900, marginLeft: 8, padding: '1px 7px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      EXTRA TIME
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <PInfoRow cells={[{ label: 'Mark Awarded', value: `${DEMO_EXTENDED_WRITING.mark} / ${DEMO_EXTENDED_WRITING.marks}` }, { label: 'Marked By', value: 'Class teacher' }, { label: 'Retained For', value: 'Form 8 review' }]} />
        <div style={{ ...P.fieldBox, marginBottom: 4 }}>
          <strong>Teacher comment:</strong> {DEMO_EXTENDED_WRITING.buildComment(student?.name)}
        </div>

        <div style={{ borderTop: '1px solid #ccc', color: '#555', fontSize: '0.72rem', marginTop: 16, paddingTop: 10 }}>
          Evidence generated by the ClassCompass JCQ Extra Time Test module. Signed and fully completed samples must be retained on file by the SENCo/Access Arrangements Coordinator for inspection purposes.
        </div>
      </div>
    </div>
  );
};

const getYearSortValue = (yearName = '') => {
  const match = String(yearName).match(/\d+/);
  return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
};

const buildStudentHierarchy = (students = []) => {
  const yearMap = new Map();

  students.forEach((student) => {
    const yearName = student.homeworkScheduleId?.yearGroup || 'Year not set';

    if (!yearMap.has(yearName)) {
      yearMap.set(yearName, { name: yearName, students: [] });
    }

    yearMap.get(yearName).students.push(student);
  });

  return Array.from(yearMap.values())
    .sort((a, b) => getYearSortValue(a.name) - getYearSortValue(b.name) || a.name.localeCompare(b.name))
    .map((year) => ({
      ...year,
      students: year.students.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    }));
};

const inputStyle = {
  background: BRAND.white,
  border: `2px solid ${BRAND.charcoal}`,
  borderRadius: '10px',
  boxSizing: 'border-box',
  color: BRAND.charcoal,
  fontFamily: "'Inter', sans-serif",
  fontSize: '0.95rem',
  padding: '10px 12px',
  width: '100%'
};

const labelStyle = {
  color: BRAND.charcoal,
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  marginBottom: '6px'
};

const sectionTitleStyle = {
  color: BRAND.coral,
  fontFamily: 'Nunito, sans-serif',
  fontSize: '0.85rem',
  fontWeight: 800,
  letterSpacing: '0.06em',
  margin: '18px 0 10px',
  textTransform: 'uppercase'
};

const JCQStudentChecklist = () => {
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [error, setError] = useState('');
  const [popup, setPopup] = useState(null); // { type, student }
  const [checklists, setChecklists] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(getChecklistStorageKey()) || 'null') || {};
    } catch (err) {
      return {};
    }
  });

  useEffect(() => {
    let isMounted = true;

    const loadStudents = async () => {
      setLoadingStudents(true);
      setError('');
      try {
        const response = await jcqAPI.getStudents();
        const studentList = Array.isArray(response?.data) ? response.data : [];
        if (!isMounted) return;
        setStudents(studentList);
      } catch (err) {
        if (isMounted) setError('Unable to load students. Please try again.');
      } finally {
        if (isMounted) setLoadingStudents(false);
      }
    };

    loadStudents();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    localStorage.setItem(getChecklistStorageKey(), JSON.stringify(checklists));
  }, [checklists]);

  const studentHierarchy = useMemo(() => buildStudentHierarchy(students), [students]);

  const updateStudentField = (studentId, field, value) => {
    setChecklists((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [field]: value }
    }));
  };

  const getTickedCount = (entry = {}) => getCheckKeysForRoute(entry.route).filter((key) => DEMO_ITEMS[key] || entry[key]).length;

  return (
    <div style={{ background: BRAND.cream, color: BRAND.charcoal, fontFamily: "'Inter', sans-serif", minHeight: '100vh', padding: '44px 20px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
        .jcq-back-link:hover { color: ${BRAND.coral}; }
        h1, h2 { font-family: 'Nunito', sans-serif; }
        .jcq-checklist-card summary { list-style: none; }
        .jcq-checklist-card summary::-webkit-details-marker { display: none; }
        .jcq-checklist-card summary:hover { color: ${BRAND.coral}; }
        .jcq-check-row { align-items: flex-start; cursor: pointer; display: flex; gap: 10px; padding: 6px 0; }
        .jcq-check-row input { accent-color: ${BRAND.coral}; height: 18px; margin-top: 2px; width: 18px; flex-shrink: 0; cursor: pointer; }
        .btn-base {
          border: none; border-radius: 10px; padding: 10px 20px;
          font-family: 'Nunito', sans-serif; font-weight: 800; cursor: pointer;
          transition: all 0.1s ease; font-size: 0.95rem;
          display: inline-flex; align-items: center; gap: 8px;
          border-bottom: 4px solid rgba(0,0,0,0.1); text-decoration: none;
        }
        .btn-base:active { transform: translateY(2px); border-bottom: 2px solid rgba(0,0,0,0.1); margin-bottom: 2px; }
        .btn-charcoal { background-color: ${BRAND.charcoal}; color: white; border: 2px solid ${BRAND.charcoal}; border-bottom: 4px solid rgba(0,0,0,0.4); }
        .btn-charcoal:active { border-bottom: 2px solid rgba(0,0,0,0.4); }
        .btn-outline { background-color: white; color: ${BRAND.charcoal}; border: 2px solid ${BRAND.charcoal}; border-bottom: 4px solid ${BRAND.charcoal}; }
        .btn-outline:active { border-bottom: 2px solid ${BRAND.charcoal}; }
        .btn-outline:hover { background-color: #F9F9F9; }
        @media print {
          body { background: #fff !important; }
          .jcq-no-print { display: none !important; }
        }
      `}</style>

      <main className={popup ? 'jcq-no-print' : undefined} style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <Link className="jcq-back-link" to="/jcq-hub" style={{ color: BRAND.charcoal, display: 'inline-block', fontWeight: 800, marginBottom: '24px', textDecoration: 'none' }}>
          ← Back to JCQ hub
        </Link>

        <p style={{ color: BRAND.coral, fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 800, letterSpacing: '0.08em', margin: '0 0 8px', textTransform: 'uppercase' }}>
          Evidence hub
        </p>
        <h1 style={{ fontSize: '2.6rem', lineHeight: 1.05, margin: 0 }}>JCQ Student Checklist</h1>
        <p style={{ color: BRAND.muted, fontSize: '1.05rem', margin: '14px 0 34px', maxWidth: '650px' }}>
          Track access arrangement paperwork for every student on your roster. Ticks and notes save automatically.
        </p>

        {error && <p style={{ color: BRAND.coral, fontWeight: 600 }}>{error}</p>}
        {loadingStudents && <p style={{ color: BRAND.muted }}>Loading students…</p>}
        {!loadingStudents && !error && students.length === 0 && <p style={{ color: BRAND.muted }}>No students found in your roster.</p>}

        {studentHierarchy.map((year) => (
          <section key={year.name} style={{ marginBottom: '30px' }}>
            <h2 style={{ fontSize: '1.35rem', margin: '0 0 14px' }}>
              {year.name} <span style={{ color: BRAND.muted, fontSize: '0.9rem', fontWeight: 600, marginLeft: '8px' }}>({year.students.length} students)</span>
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {year.students.map((student) => {
                const entry = checklists[student._id] || {};
                const sections = getSectionsForRoute(entry.route);
                const totalKeys = getCheckKeysForRoute(entry.route).length;
                const ticked = getTickedCount(entry);

                return (
                  <details key={student._id} className="jcq-checklist-card" style={{ background: BRAND.white, border: `2px solid ${BRAND.charcoal}`, borderRadius: '16px', boxShadow: '5px 5px 0 rgba(45, 64, 66, 0.1)', padding: '18px 24px' }}>
                    <summary style={{ alignItems: 'center', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: '16px', fontFamily: 'Nunito, sans-serif', fontSize: '1.15rem', fontWeight: 800 }}>
                      <span>{student.name}</span>
                      <span style={{ background: totalKeys > 0 && ticked === totalKeys ? 'rgba(117, 185, 190, 0.25)' : 'rgba(45, 64, 66, 0.08)', borderRadius: '999px', color: BRAND.charcoal, fontSize: '0.8rem', fontWeight: 800, padding: '5px 12px', whiteSpace: 'nowrap' }}>
                        {totalKeys > 0 ? `${ticked}/${totalKeys} ticked` : 'No route selected'}
                      </span>
                    </summary>

                    <div style={{ borderTop: `1px solid rgba(45, 64, 66, 0.15)`, marginTop: '16px', paddingTop: '16px' }}>
                      <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                        <div>
                          <span style={labelStyle}>Candidate name:</span>
                          <input type="text" value={student.name || ''} readOnly style={{ ...inputStyle, background: BRAND.cream }} />
                        </div>
                        <div>
                          <label style={labelStyle} htmlFor={`need-${student._id}`}>Primary area of need for access arrangements:</label>
                          <input
                            id={`need-${student._id}`}
                            type="text"
                            value={entry.primaryNeed || ''}
                            onChange={(e) => updateStudentField(student._id, 'primaryNeed', e.target.value)}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle} htmlFor={`arrangements-${student._id}`}>Access arrangements:</label>
                          <input
                            id={`arrangements-${student._id}`}
                            type="text"
                            value={entry.accessArrangements || ''}
                            onChange={(e) => updateStudentField(student._id, 'accessArrangements', e.target.value)}
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: '18px' }}>
                        <span style={labelStyle}>Route:</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                          {ROUTE_OPTIONS.map((option) => {
                            const active = entry.route === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => updateStudentField(student._id, 'route', option.value)}
                                style={{
                                  background: active ? BRAND.coral : BRAND.white,
                                  border: `2px solid ${active ? BRAND.coral : BRAND.charcoal}`,
                                  borderRadius: '999px',
                                  color: active ? BRAND.white : BRAND.charcoal,
                                  cursor: 'pointer',
                                  fontFamily: 'Nunito, sans-serif',
                                  fontSize: '0.85rem',
                                  fontWeight: 800,
                                  padding: '8px 16px'
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {sections.length === 0 && (
                        <p style={{ color: BRAND.muted, fontSize: '0.95rem', margin: '18px 0 0' }}>
                          Select a route above to show this student's checklist.
                        </p>
                      )}

                      {sections.map((section) => (
                        <div key={section.title}>
                          <p style={sectionTitleStyle}>{section.title}</p>
                          {section.items.map((item) => {
                            const demo = DEMO_ITEMS[item.key];

                            if (demo) {
                              return (
                                <div key={item.key}>
                                  <div className="jcq-check-row" style={{ cursor: 'default' }}>
                                    <input type="checkbox" checked readOnly style={{ cursor: 'default' }} />
                                    <span style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                                      {item.label}
                                      <button
                                        type="button"
                                        onClick={() => setPopup({ type: demo.popupType, student })}
                                        style={{ background: 'rgba(117, 185, 190, 0.18)', border: `1.5px solid ${BRAND.teal}`, borderRadius: '999px', color: BRAND.charcoal, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', fontWeight: 800, marginLeft: '10px', padding: '4px 12px', verticalAlign: 'middle' }}
                                      >
                                        {demo.buttonLabel}
                                      </button>
                                    </span>
                                  </div>
                                  {demo.popupType === 'evidence25' && (
                                    <details style={{ margin: '2px 0 8px 28px' }}>
                                      <summary style={{ color: BRAND.muted, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700 }}>
                                        {DEMO_EVIDENCE_SUBJECTS.length} subjects submitted
                                      </summary>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                                        {DEMO_EVIDENCE_SUBJECTS.map((sample) => (
                                          <div key={sample.subject} style={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.85rem' }}>
                                            <span aria-hidden="true" style={{ color: '#1F7A3A', fontWeight: 900 }}>✓</span>
                                            <span style={{ color: BRAND.charcoal, fontWeight: 700 }}>{sample.subject}</span>
                                            <span style={{ color: BRAND.muted, fontWeight: 600 }}>Submitted {formatDemoSubmissionDate(sample.submittedDaysAgo)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <label key={item.key} className="jcq-check-row">
                                <input
                                  type="checkbox"
                                  checked={Boolean(entry[item.key])}
                                  onChange={(e) => updateStudentField(student._id, item.key, e.target.checked)}
                                />
                                <span style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>{item.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      ))}

                      {sections.length > 0 && (
                        <div style={{ marginTop: '14px', maxWidth: '320px' }}>
                          <label style={labelStyle} htmlFor={`aao-expiry-${student._id}`}>Expiry date for AAO Approval (record the date here):</label>
                          <input
                            id={`aao-expiry-${student._id}`}
                            type="date"
                            value={entry.aaoExpiryDate || ''}
                            onChange={(e) => updateStudentField(student._id, 'aaoExpiryDate', e.target.value)}
                            style={inputStyle}
                          />
                        </div>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      {popup?.type === 'form8Part1' && (
        <Form8Modal
          baseline={{ ...DEMO_BASELINE, name: popup.student?.name }}
          difficultiesText={buildDemoDifficultiesText(popup.student?.name)}
          nwowText={buildDemoNWoWText(popup.student?.name)}
          onClose={() => setPopup(null)}
        />
      )}
      {popup?.type === 'evidence25' && <Evidence25Popup student={popup.student} onClose={() => setPopup(null)} />}
    </div>
  );
};

export default JCQStudentChecklist;
